import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import { t } from 'i18next'
import { GET_SEARCH_TRIP } from 'graphql/common/queries'
import { BookingSeatSelection } from '../components/seatSelectLayout'
import { RESCHEDULING_BOOKING } from 'graphql/booking/mutations'
import { ArrowLeftIcon, CalendarIcon, InformationCircleIcon } from '@heroicons/react/24/solid'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'
import loadingImage from 'assets/images/boat-loading.gif'
import { customTimeFormat, getTimeDifference } from 'utils/common'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { BoatBookingImage, BoatIcon } from 'components/icons'
import stSeat from 'assets/images/icons/st_seat.svg'
import vipSeat from 'assets/images/icons/vip_seat.svg'

// Helper function to format duration
function formatDuration(minutes) {
    if (!minutes) return ''
    
    if (minutes < 60) {
        return `${minutes} ${minutes === 1 ? t('minute') : t('minutes')}`
    }
    
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    
    if (remainingMinutes === 0) {
        return `${hours} ${hours === 1 ? t('hour') : t('hours')}`
    }
    
    return `${hours}${t('hr')} ${remainingMinutes}${t('mins')}`
}

export default function RescheduleBooking() {
    const navigate = useNavigate()
    const location = useLocation()
    const { bookingId, segment, bookingInfo } = location.state || {}

    const [searchDate, setSearchDate] = useState('')
    const [availableTrips, setAvailableTrips] = useState([])
    const [selectedTrip, setSelectedTrip] = useState(null)
    const [seatResult, setSeatResult] = useState(null)
    const [loadingMessage, setLoadingMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    
    // Ref for scrolling to next section
    const nextStepsRef = useRef(null)

    // Validate required state
    useEffect(() => {
        if (!bookingId || !segment) {
            navigate('/manage-boat-booking')
        }
        // default date to segment travel date
        if (segment?.travel_date) {
            setSearchDate(segment.travel_date)
        }
    }, [bookingId, segment])

    // Prefilled, locked details
    const fromLocationId = segment?.start?.location_id
    const toLocationId = segment?.end?.location_id
    const passengerCount = segment?.passengers?.length || 0

    // Build read-only passenger list for seat selection component
    const passengerDetails = useMemo(() => {
        const passengers = (segment?.passengers || []).map(p => ({
            passenger: {
                given_name: p.passenger?.given_name,
                family_name: p.passenger?.family_name,
                date_of_birth: p.passenger?.date_of_birth,
                nationality_code: p.passenger?.nationality_code || null,
                special_needs: Boolean(p.passenger?.special_needs),
                medical_condition: Boolean(p.passenger?.medical_condition),
                medical_remarks: p.passenger?.medical_remarks || null,
            },
            seat_info: {
                seat_no: null, // force re-selection
                seat_type_code: '',
                age_group_code: p.passenger?.age_group_code, // needed for labels
            },
        }))
        return { passengers }
    }, [segment])

    // Search available trips for selected date
    const [getTrips, { loading: tripsLoading }] = useLazyQuery(GET_SEARCH_TRIP, {
        context: { unauth: true },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data?.searchTrip?.success && data?.searchTrip?.code === '200') {
                // Filter to exact direction (start/end match)
                const trips = (data.searchTrip.trips || []).filter(trip =>
                    trip.start?.location_id === fromLocationId && trip.end?.location_id === toLocationId
                )
                setAvailableTrips(trips)
            } else {
                setAvailableTrips([])
                setDialogMessage(data?.searchTrip?.message || t('Failed to fetch trips'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setAvailableTrips([])
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const searchTrips = () => {
        if (!searchDate || !fromLocationId || !toLocationId) {
            setDialogMessage(t('Please select a date'))
            setShowErrDialog(true)
            return
        }
        const searchfilter = [
            {
                from_location_id: fromLocationId,
                to_location_id: toLocationId,
                trip_type: 'DEPART',
                trip_date: searchDate,
            },
        ]
        getTrips({ variables: { searchfilter, prefered_current_type: 'BHD', bookingTripType: 'ONE_WAY' } })
    }

    // Prepare tripDetails for seat selector when a trip is chosen
    const tripDetails = useMemo(() => {
        if (!selectedTrip) return null
        return {
            tripId: selectedTrip.trip_id,
            startLocation: selectedTrip.start?.location_id,
            endLocation: selectedTrip.end?.location_id,
            departureDate: selectedTrip.trip_date,
            routeId: selectedTrip.route_id,
            isRoundTrip: false,
            isInternational: false,
        }
    }, [selectedTrip])

    const handleSeatSelectionDone = result => {
        // result: { passengers, totalCost, priceBreakdown }
        setSeatResult(result)
    }

    // Mutation: reschedule
    const [rescheduleBooking, { loading: rescheduleLoading }] = useMutation(RESCHEDULING_BOOKING, {
        onCompleted: data => {
            const res = data?.reschedulingBooking
            if (res?.success && res?.code === '200') {
                setShowSucDialog(true)
                setDialogMessage(res?.message || t('Rescheduled successfully'))
                // redirect back to booking details (use the new segment travel date if available)
                const newDate = selectedTrip?.trip_date || segment?.travel_date
                setTimeout(() => {
                    navigate(`/manage-boat-booking/details/${bookingId}/${newDate}`)
                }, 800)
            } else {
                setDialogMessage(res?.message || t('Failed to reschedule booking'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const buildPayload = () => {
        if (!seatResult || !selectedTrip) return null
        // Create passengers array for payload using selected seats
        const passengers = seatResult.passengers.map(info => ({
            passenger: {
                given_name: info.passenger.given_name,
                family_name: info.passenger.family_name,
                date_of_birth: info.passenger.date_of_birth,
                email: null,
                phone_number: null,
                document_type: null,
                document_number: null,
                age_group_code: info.seat_info.age_group_code,
                nationality_code: info.passenger.nationality_code || null,
                special_needs: Boolean(info.passenger.special_needs),
                medical_condition: Boolean(info.passenger.medical_condition),
                medical_remarks: info.passenger.medical_remarks || null,
                document: null,
            },
            seat_info: {
                seat_no: info.seat_info.seat_no,
                seat_type_code: info.seat_info.seat_type_code,
            },
        }))

        const trip_segment = {
            segment_id: segment.segment_id,
            segment_order: segment.segment_order || 1,
            route_id: selectedTrip.route_id,
            trip_id: selectedTrip.trip_id,
            travel_date: selectedTrip.trip_date,
            trip_location: {
                start_location_id: selectedTrip.start?.location_id,
                end_location_id: selectedTrip.end?.location_id,
            },
            passengers,
            meals: [],
            luggage_add_on: { extra_luggage: [], special_items: [] },
            shuttle_service_add_on: false,
        }

        // generate simple device id
        const deviceId = (() => {
            const key = 'device_id'
            const existing = localStorage.getItem(key)
            if (existing) return existing
            const newId = 'device_' + Date.now()
            localStorage.setItem(key, newId)
            return newId
        })()

        return {
            bookingId: bookingId,
            booking: {
                trip_segments: [trip_segment],
                metadata: {
                    booking_id: bookingInfo?.booking_id || bookingId,
                    device_id: deviceId,
                    metadata_prefered_current_type: 'BHD',
                },
            },
        }
    }

    const handleConfirm = () => {
        if (!selectedTrip) {
            setDialogMessage(t('Please select a trip'))
            setShowErrDialog(true)
            return
        }
        if (!seatResult || !seatResult.passengers || seatResult.passengers.some(p => !p.seat_info?.seat_no)) {
            setDialogMessage(t('Please select seats for all passengers'))
            setShowErrDialog(true)
            return
        }
        const payload = buildPayload()
        if (!payload) return
        setLoadingMessage(t('Rescheduling...'))
        rescheduleBooking({ variables: payload })
    }

    const handleBack = () => {
        navigate(-1)
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button className="inline-flex items-center gap-x-1.5 rounded-md text-sm" onClick={handleBack}>
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                    {t('Back')}
                </button>
                <h3 className="text-lg font-bold leading-7 text-primary">{t('Reschedule Trip')}</h3>
            </div>

            {/* Locked summary */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 text-center text-sm gap-y-4 md:gap-y-0 text-gray-600">
                    <div className="flex">
                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        <div className="ps-3 text-start">
                            <p className="pb-1">{t('From Location')}</p>
                            <p className="font-bold text-[16px] break-words">
                                {segment?.start?.location?.name?.en || 'N/A'}
                            </p>
                        </div>
                    </div>
                    <div className="flex">
                        <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        <div className="ps-3 text-start">
                            <p className="pb-1">{t('To Location')}</p>
                            <p className="font-bold text-[16px] break-words">
                                {segment?.end?.location?.name?.en || 'N/A'}
                            </p>
                        </div>
                    </div>
                    <div className="flex">
                        <CalendarIcon className="w-5 h-5 mb-1 text-[#285f74]" />
                        <div className="ps-3 text-start">
                            <p className="pb-1">{t('Travel Date')}</p>
                            <p className="font-bold text-[15px]">
                                {segment?.travel_date ? new Date(segment.travel_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                            </p>
                            {segment?.trip?.duration_minutes && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('Duration')}: {formatDuration(segment.trip.duration_minutes)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Step 1: Pick date and trip */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">{t('Select Date')}</label>
                        <Flatpickr
                            value={searchDate}
                            onChange={date => setSearchDate(date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '')}
                            options={{ altInput: true, altFormat: 'Y/m/d', dateFormat: 'Y/m/d', minDate: 'today' }}
                            className="block w-full rounded-md border border-gray-300 py-2.5 text-gray-900 shadow-sm"
                        />
                    </div>
                    <div>
                        <button
                            onClick={searchTrips}
                            className="mt-6 md:mt-0 w-full md:w-auto hover:bg-primary/[.9] bg-primary focus-visible:outline-primary rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm"
                        >
                            {t('Search Trips')}
                        </button>
                    </div>
                </div>

                {(tripsLoading) && (
                    <div className="flex flex-col justify-center items-center bg-white h-40 mt-4">
                        <img alt="logo" src={loadingImage} className="w-[120px]" />
                        <p className="pt-2 text-primary font-semibold">{t('Loading, please wait...')}</p>
                    </div>
                )}

                {!tripsLoading && availableTrips?.length === 0 && searchDate && (
                    <div className="text-center py-12 bg-gray-50 mt-4 rounded-lg">
                        <h3 className="text-sm font-semibold text-gray-900">{t('No Trips Found')}</h3>
                        <p className="mt-1 text-sm text-gray-500">{t('No trips available for the selected date')}</p>
                    </div>
                )}

                {!tripsLoading && availableTrips?.length > 0 && (
                    <div className="mt-4 space-y-4">
                        {availableTrips.map(trip => {
                            const isSelected = selectedTrip?.trip_id === trip.trip_id && selectedTrip?.trip_date === trip.trip_date
                            return (
                                <div 
                                    key={`${trip.trip_id}-${trip.trip_date}`} 
                                    className={`border-2 rounded-lg overflow-hidden bg-white shadow-md ${
                                        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200'
                                    }`}
                                >
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row justify-between gap-4">
                                            {/* Trip Info Section */}
                                            <div className="flex gap-4 items-start">
                                                <BoatBookingImage className="w-12 h-12 fill-primary flex-shrink-0" />
                                                <div>
                                                    <h4 className="text-lg font-bold text-gray-900 mb-1">
                                                        {trip.trip?.trip_name?.en || 'Trip'}
                                                    </h4>
                                                    <span className="inline-flex items-center gap-x-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white mb-2">
                                                        <InformationCircleIcon className="h-4 w-4" />
                                                        {trip.route?.route_name?.en || trip.route_id}
                                                    </span>
                                                    {trip.trip_date && (
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            {t('Departure Date')}: {new Date(trip.trip_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Time and Route Section */}
                                            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 flex-1">
                                                <div className="flex items-center gap-4 md:gap-6 w-full md:w-auto">
                                                    {/* Start Time */}
                                                    <div className="text-center">
                                                        <p className="text-xl font-bold text-gray-900">
                                                            {customTimeFormat(trip.start?.arrival_time)}
                                                        </p>
                                                        <p className="text-sm text-gray-600 mt-1">{trip.start?.location?.name?.en}</p>
                                                    </div>

                                                    {/* Route Line with Duration */}
                                                    <div className="flex-1 min-w-[120px] relative">
                                                        <div className="text-xs text-gray-500 text-center mb-2 whitespace-nowrap">
                                                            {trip.duration_minutes ? formatDuration(trip.duration_minutes) : getTimeDifference(trip.start?.arrival_time, trip.end?.arrival_time)} | 0 stops
                                                        </div>
                                                        <div className="flex items-center">
                                                            <span className="border-primary border-2 w-3 h-3 rounded-full bg-primary"></span>
                                                            <div className="flex-1 h-0.5 bg-gray-300 mx-2"></div>
                                                            <span className="border-primary border-2 w-3 h-3 rounded-full bg-primary"></span>
                                                        </div>
                                                    </div>

                                                    {/* End Time */}
                                                    <div className="text-center">
                                                        <p className="text-xl font-bold text-gray-900">
                                                            {customTimeFormat(trip.end?.arrival_time)}
                                                        </p>
                                                        <p className="text-sm text-gray-600 mt-1">{trip.end?.location?.name?.en}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Available Seats and Select Button */}
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mt-4 pt-4 border-t border-gray-100 gap-3">
                                            <div className="flex items-center gap-4">
                                                <div className="text-sm text-gray-700 font-medium">{t('Available Seats')}</div>
                                                {trip.available_seats && trip.available_seats.length > 0 && (
                                                    <div className="flex gap-3">
                                                        {trip.available_seats.map(seat => (
                                                            <div key={seat.code} className="flex items-center gap-1.5">
                                                                <img 
                                                                    src={seat.code === 'STD' ? stSeat : vipSeat} 
                                                                    alt={seat.code} 
                                                                    className="w-5 h-5"
                                                                />
                                                                <span className="text-sm font-semibold text-gray-900">{seat.available}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setSelectedTrip(trip)
                                                    // Scroll to next steps section after a brief delay
                                                    setTimeout(() => {
                                                        nextStepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                                    }, 100)
                                                }}
                                                className="hover:bg-primary/90 bg-primary rounded-md px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors w-full md:w-auto"
                                            >
                                                {t('Select')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* Next Steps Section Header */}
            {selectedTrip && (
                <div ref={nextStepsRef} className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border-2 border-primary/20 p-6 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                            <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center">
                                <InformationCircleIcon className="h-6 w-6 text-white" />
                            </div>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('Fill the below information')}</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {t('Review passenger details, select seats, and confirm your reschedule')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 2: Passenger Review */}
            {selectedTrip && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Passenger Details')}</h3>
                    <p className="text-sm text-gray-600 mb-4">{t('The following passengers will be rescheduled')}:</p>
                    <div className="space-y-3">
                        {segment?.passengers?.map((passenger, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            {t('Passenger')} {idx + 1}:
                                        </span>
                                        <span className="text-base font-semibold text-gray-900">
                                            {passenger.passenger?.given_name} {passenger.passenger?.family_name}
                                        </span>
                                    </div>
                                    {passenger.passenger?.age_group_code && (
                                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 mt-2">
                                            {passenger.passenger.age_group_code === 'ADL' ? t('Adult') : 
                                             passenger.passenger.age_group_code === 'CHD' ? t('Child') : 
                                             passenger.passenger.age_group_code === 'INF' ? t('Infant') : 
                                             passenger.passenger.age_group_code === 'SNR' ? t('Senior') : 
                                             passenger.passenger.age_group_code}
                                        </span>
                                    )}
                                </div>
                                {passenger.passenger?.special_needs && (
                                    <span className="text-xs text-orange-600 font-medium">{t('Special Needs')}</span>
                                )}
                                {passenger.passenger?.medical_condition && (
                                    <span className="text-xs text-red-600 font-medium">{t('Medical Condition')}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Step 3: Seat Selection */}
            {selectedTrip && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Seat Selection')}</h3>
                    <BookingSeatSelection
                        tripDetails={tripDetails}
                        passengerDetails={passengerDetails}
                        priceDetails={[]}
                        availableSeatTypeCount={[]}
                        onSeatSelection={handleSeatSelectionDone}
                        disablePriceCalculation={true}
                    />
                </div>
            )}

            {/* Step 4: Confirm */}
            {selectedTrip && seatResult && (
                <div className="bg-white rounded-lg border-2 border-primary/30 shadow-lg p-6 mb-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex items-start gap-3">
                            <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 mb-1">
                                    {t('Ready to Confirm')}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {t('No price changes will be applied for rescheduling')}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleConfirm}
                            disabled={rescheduleLoading}
                            className={`hover:bg-primary/90 bg-primary rounded-md px-8 py-3 text-base font-semibold text-white shadow-sm transition-colors w-full md:w-auto ${
                                rescheduleLoading ? 'opacity-75 cursor-not-allowed' : ''
                            }`}
                        >
                            {rescheduleLoading ? t('Confirming...') : t('Confirm Reschedule')}
                        </button>
                    </div>
                </div>
            )}

            {showSucDialog && (
                <SuccessDialog status={true} title={t('Success')} message={dialogMessage} onClose={() => setShowSucDialog(false)} />
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title={t('Error')} message={dialogMessage} onClose={() => setShowErrDialog(false)} />
            )}
        </>
    )
}

